import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Video, Wand2, Loader2, Download, Share2, Trash2, X, ImagePlus, Crown, Volume2 } from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/i18n";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const DURATIONS = ["5", "10", "15", "20", "30"];
const ASPECTS = ["16:9", "9:16", "1:1"];
const VOICES = ["onyx", "nova", "alloy", "echo", "fable", "shimmer"];
const TRANSITIONS = [
  { value: "fade", key: "video_tr_fade" },
  { value: "dissolve", key: "video_tr_dissolve" },
  { value: "slideright", key: "video_tr_slide" },
  { value: "wiperight", key: "video_tr_wipe" },
  { value: "circleopen", key: "video_tr_circle" },
  { value: "radial", key: "video_tr_radial" },
];
const resolveUrl = (u) => (u && u.startsWith("/") ? process.env.REACT_APP_BACKEND_URL + u : u);

export const VideoStudioModal = ({ open, onOpenChange, onRequestUpgrade }) => {
  const { t } = useLanguage();
  const [prompt, setPrompt] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [duration, setDuration] = useState("5");
  const [aspect, setAspect] = useState("16:9");
  const [narration, setNarration] = useState("");
  const [voice, setVoice] = useState("onyx");
  const [subtitles, setSubtitles] = useState(false);
  const [transition, setTransition] = useState("fade");
  const [generating, setGenerating] = useState(false);
  const [jobs, setJobs] = useState([]);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/video/jobs`);
      setJobs(res.data.jobs || []);
    } catch (e) {
      console.warn("video jobs fetch failed", e);
    }
  }, []);

  useEffect(() => {
    if (open) fetchJobs();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, fetchJobs]);

  // Poll while any job is pending
  useEffect(() => {
    const pending = jobs.some((j) => j.status === "queued" || j.status === "processing");
    if (open && pending && !pollRef.current) {
      pollRef.current = setInterval(fetchJobs, 6000);
    } else if ((!pending || !open) && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [jobs, open, fetchJobs]);

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setImagePreview(ev.target.result); setImageBase64(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error(t("video_need_prompt")); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/video/generate`, {
        prompt: prompt.trim(),
        image_base64: imageBase64 || undefined,
        duration,
        aspect_ratio: aspect,
        narration: narration.trim() || undefined,
        voice,
        subtitles,
        transition,
      });
      toast.success(t("video_started"));
      setJobs((prev) => [res.data.job, ...prev]);
      setPrompt("");
      setNarration("");
      setImagePreview(null);
      setImageBase64(null);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error(t("video_premium_only"));
        if (onRequestUpgrade) onRequestUpgrade();
      } else {
        toast.error(e?.response?.data?.detail || t("video_failed"));
      }
    } finally {
      setGenerating(false);
    }
  };

  const downloadVideo = (url) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = "kaangpt-video.mp4";
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const shareVideo = async (url) => {
    try {
      if (navigator.share) {
        await navigator.share({ title: "KaanGPT Video", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(t("video_link_copied"));
      }
    } catch (e) { /* user cancelled */ }
  };

  const deleteJob = async (id) => {
    try {
      await axios.delete(`${API}/video/jobs/${id}`);
      setJobs((prev) => prev.filter((j) => j.id !== id));
    } catch (e) { toast.error(t("video_failed")); }
  };

  const statusLabel = (s) => ({
    queued: t("video_status_queued"),
    processing: t("video_status_processing"),
    completed: t("video_status_completed"),
    failed: t("video_status_failed"),
  }[s] || s);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto bg-[var(--bg-secondary)] border-[var(--border-color)]" data-testid="video-studio-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[var(--text-primary)]">
            <Video size={20} className="text-purple-400" /> {t("video_studio_title")}
            <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-full">
              <Crown size={12} /> Pro
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-[var(--text-secondary)]">{t("video_studio_desc")}</p>

          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={t("video_prompt_placeholder")}
            rows={3}
            data-testid="video-prompt-input"
            className="w-full bg-[var(--input-bg)] border border-[var(--border-color)] rounded-lg p-3 text-sm text-[var(--text-primary)] resize-none focus:outline-none focus:ring-1 focus:ring-purple-500"
          />

          {/* Optional image for image-to-video */}
          <div className="flex items-center gap-3">
            {imagePreview ? (
              <div className="relative">
                <img src={imagePreview} alt="ref" className="w-16 h-16 object-cover rounded-lg" />
                <button
                  onClick={() => { setImagePreview(null); setImageBase64(null); }}
                  className="absolute -top-2 -right-2 bg-[var(--bg-tertiary)] rounded-full p-0.5 text-red-400"
                  data-testid="video-image-remove"
                ><X size={14} /></button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                className="border-[var(--border-color)] text-[var(--text-secondary)]"
                data-testid="video-image-upload-btn"
              >
                <ImagePlus size={16} className="mr-1" /> {t("video_add_image")}
              </Button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImage} data-testid="video-image-input" />
            <span className="text-xs text-[var(--text-muted)]">{t("video_image_hint")}</span>
          </div>

          {/* Settings */}
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="text-xs text-[var(--text-muted)] block mb-1">{t("video_duration")}</label>
              <div className="flex gap-2">
                {DURATIONS.map((d) => (
                  <button
                    key={d}
                    onClick={() => setDuration(d)}
                    data-testid={`video-duration-${d}`}
                    className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${duration === d ? "bg-purple-600 border-purple-600 text-white" : "border-[var(--border-color)] text-[var(--text-secondary)]"}`}
                  >{d}s</button>
                ))}
              </div>
              <p className="text-[11px] text-[var(--text-muted)] mt-1">{t("video_duration_hint")}</p>
            </div>
            {parseInt(duration, 10) >= 15 && (
              <div data-testid="video-transition-wrap">
                <label className="text-xs text-[var(--text-muted)] block mb-1">{t("video_transition")}</label>
                <select
                  value={transition}
                  onChange={(e) => setTransition(e.target.value)}
                  data-testid="video-transition-select"
                  className="bg-[var(--input-bg)] border border-[var(--border-color)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none"
                >
                  {TRANSITIONS.map((tr) => (
                    <option key={tr.value} value={tr.value}>{t(tr.key)}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs text-[var(--text-muted)] block mb-1">{t("video_aspect")}</label>
              <div className="flex gap-2">
                {ASPECTS.map((a) => (
                  <button
                    key={a}
                    onClick={() => setAspect(a)}
                    data-testid={`video-aspect-${a.replace(":", "-")}`}
                    className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${aspect === a ? "bg-purple-600 border-purple-600 text-white" : "border-[var(--border-color)] text-[var(--text-secondary)]"}`}
                  >{a}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Voiceover (OpenAI TTS) + subtitles */}
          <div className="space-y-3 bg-[var(--bg-tertiary)]/30 rounded-lg p-3 border border-[var(--border-color)]">
            <div className="flex items-center gap-2 text-sm font-medium text-[var(--text-primary)]">
              <Volume2 size={16} className="text-purple-400" /> {t("video_voiceover")}
            </div>
            <textarea
              value={narration}
              onChange={(e) => setNarration(e.target.value)}
              placeholder={t("video_narration_placeholder")}
              rows={2}
              data-testid="video-narration-input"
              className="w-full bg-[var(--input-bg)] border border-[var(--border-color)] rounded-lg p-2.5 text-sm text-[var(--text-primary)] resize-none focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <label className="text-xs text-[var(--text-muted)] block mb-1">{t("video_voice")}</label>
                <select
                  value={voice}
                  onChange={(e) => setVoice(e.target.value)}
                  data-testid="video-voice-select"
                  className="bg-[var(--input-bg)] border border-[var(--border-color)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none"
                >
                  {VOICES.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={subtitles} onCheckedChange={setSubtitles} data-testid="video-subtitles-toggle" />
                <span className="text-sm text-[var(--text-secondary)]">{t("video_subtitles")}</span>
              </div>
            </div>
            <p className="text-[11px] text-[var(--text-muted)]">{t("video_voiceover_hint")}</p>
          </div>

          <Button
            onClick={generate}
            disabled={generating}
            className="w-full bg-purple-600 hover:bg-purple-500 text-white"
            data-testid="video-generate-btn"
          >
            {generating ? <Loader2 size={16} className="animate-spin mr-2" /> : <Wand2 size={16} className="mr-2" />}
            {t("video_generate")}
          </Button>

          {/* Jobs list */}
          {jobs.length > 0 && (
            <div className="space-y-3 pt-2">
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t("video_my_videos")}</h3>
              {jobs.map((j) => (
                <div key={j.id} className="bg-[var(--bg-tertiary)]/40 rounded-lg p-3" data-testid="video-job-card">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-sm text-[var(--text-primary)] truncate flex-1">{j.prompt}</p>
                    <button onClick={() => deleteJob(j.id)} data-testid="video-job-delete" className="text-[var(--text-muted)] hover:text-red-400 shrink-0">
                      <Trash2 size={16} />
                    </button>
                  </div>
                  {j.status === "completed" && j.video_url ? (
                    <div className="space-y-2">
                      <video src={resolveUrl(j.video_url)} controls className="w-full rounded-lg max-h-72 bg-black" data-testid="video-player" />
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => downloadVideo(resolveUrl(j.video_url))} data-testid="video-download" className="border-[var(--border-color)]">
                          <Download size={14} className="mr-1" /> {t("video_download")}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => shareVideo(resolveUrl(j.video_url))} data-testid="video-share" className="border-[var(--border-color)]">
                          <Share2 size={14} className="mr-1" /> {t("video_share")}
                        </Button>
                      </div>
                    </div>
                  ) : j.status === "failed" ? (
                    <p className="text-xs text-red-400" data-testid="video-job-status">⚠ {statusLabel(j.status)} — {j.error || ""}</p>
                  ) : (
                    <p className="text-xs text-purple-300 flex items-center gap-2" data-testid="video-job-status">
                      <Loader2 size={14} className="animate-spin" /> {statusLabel(j.status)}{j.progress ? ` ${j.progress}` : ""} ({t("video_wait_hint")})
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VideoStudioModal;
